import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";

// Blocky cartoon zombie - green skin, big eyes, messy hair, torn shirt
function Zombie({ zombie }) {
  const groupRef = useRef();
  const headRef = useRef();
  const legLRef = useRef();
  const legRRef = useRef();
  const armLRef = useRef();
  const armRRef = useRef();
  const phase = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame((state) => {
    if (!groupRef.current || !zombie) return;
    const t = state.clock.elapsedTime;
    groupRef.current.position.set(zombie.position[0], zombie.position[1], zombie.position[2]);
    groupRef.current.rotation.y = zombie.rotation;

    // Walking animation - slow waddle
    const walkSpeed = zombie.beingPulled ? 0 : 4;
    const swing = Math.sin(t * walkSpeed + phase) * 0.4;
    if (legLRef.current) legLRef.current.rotation.x = swing;
    if (legRRef.current) legRRef.current.rotation.x = -swing;
    if (armLRef.current) armLRef.current.rotation.x = -swing * 0.7;
    if (armRRef.current) armRRef.current.rotation.x = swing * 0.7;

    // Head bobble
    if (headRef.current) {
      headRef.current.rotation.z = Math.sin(t * 2 + phase) * 0.1;
    }
  });

  if (!zombie) return null;

  const t = zombie.typeData;
  const size = t.size;
  const skin = t.color;
  const isBoss = t.boss;
  const glow = t.glow;

  return (
    <group ref={groupRef}>
      <group scale={[size, size, size]}>
        {/* Body - torn purple/dark shirt */}
        <mesh position={[0, 0.85, 0]} castShadow>
          <boxGeometry args={[0.85, 1.0, 0.55]} />
          <meshStandardMaterial color={isBoss ? "#3a1a4a" : "#4a2a5a"} roughness={0.9} />
        </mesh>
        {/* Torn shirt bottom (zigzag effect via small cubes) */}
        <mesh position={[-0.3, 0.32, 0]} castShadow>
          <boxGeometry args={[0.25, 0.18, 0.55]} />
          <meshStandardMaterial color={isBoss ? "#3a1a4a" : "#4a2a5a"} roughness={0.9} />
        </mesh>
        <mesh position={[0.25, 0.28, 0]} castShadow>
          <boxGeometry args={[0.28, 0.22, 0.55]} />
          <meshStandardMaterial color={isBoss ? "#3a1a4a" : "#4a2a5a"} roughness={0.9} />
        </mesh>

        {/* Head */}
        <group ref={headRef} position={[0, 1.7, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.75, 0.75, 0.75]} />
            <meshStandardMaterial
              color={skin}
              emissive={glow ? skin : "#000000"}
              emissiveIntensity={glow ? 0.5 : 0}
              roughness={0.7}
            />
          </mesh>
          {/* Messy hair tufts (dark) */}
          <mesh position={[-0.18, 0.42, 0.05]}>
            <boxGeometry args={[0.22, 0.18, 0.4]} />
            <meshStandardMaterial color="#1a0e0e" roughness={1} />
          </mesh>
          <mesh position={[0.2, 0.4, -0.08]}>
            <boxGeometry args={[0.28, 0.2, 0.36]} />
            <meshStandardMaterial color="#1a0e0e" roughness={1} />
          </mesh>
          {/* Big round eyes - white */}
          <mesh position={[-0.18, 0.05, 0.38]}>
            <sphereGeometry args={[0.14, 12, 12]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          <mesh position={[0.18, 0.05, 0.38]}>
            <sphereGeometry args={[0.14, 12, 12]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          {/* Pupils */}
          <mesh position={[-0.18, 0.05, 0.49]}>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshStandardMaterial color="#0a0a0a" />
          </mesh>
          <mesh position={[0.18, 0.05, 0.49]}>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshStandardMaterial color="#0a0a0a" />
          </mesh>
          {/* Funny mouth - small dark rectangle */}
          <mesh position={[0, -0.18, 0.39]}>
            <boxGeometry args={[0.32, 0.08, 0.02]} />
            <meshStandardMaterial color="#1a0a0a" />
          </mesh>
          {/* Single tooth */}
          <mesh position={[-0.05, -0.22, 0.4]}>
            <boxGeometry args={[0.06, 0.08, 0.02]} />
            <meshStandardMaterial color="#fff8d6" />
          </mesh>
        </group>

        {/* Arms */}
        <group ref={armLRef} position={[-0.5, 1.3, 0]}>
          <mesh position={[0, -0.35, 0]} castShadow>
            <boxGeometry args={[0.2, 0.7, 0.2]} />
            <meshStandardMaterial color={skin} roughness={0.8} />
          </mesh>
        </group>
        <group ref={armRRef} position={[0.5, 1.3, 0]}>
          <mesh position={[0, -0.35, 0]} castShadow>
            <boxGeometry args={[0.2, 0.7, 0.2]} />
            <meshStandardMaterial color={skin} roughness={0.8} />
          </mesh>
        </group>

        {/* Legs */}
        <group ref={legLRef} position={[-0.22, 0.35, 0]}>
          <mesh position={[0, -0.18, 0]} castShadow>
            <boxGeometry args={[0.24, 0.36, 0.24]} />
            <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
          </mesh>
        </group>
        <group ref={legRRef} position={[0.22, 0.35, 0]}>
          <mesh position={[0, -0.18, 0]} castShadow>
            <boxGeometry args={[0.24, 0.36, 0.24]} />
            <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
          </mesh>
        </group>

        {/* Glow aura for glowing/boss */}
        {glow && (
          <pointLight position={[0, 1.2, 0]} color={skin} intensity={1} distance={6} />
        )}
        {/* Boss crown */}
        {isBoss && (
          <mesh position={[0, 2.3, 0]} castShadow>
            <coneGeometry args={[0.4, 0.4, 8]} />
            <meshStandardMaterial
              color="#ffd23f"
              emissive="#ffb43f"
              emissiveIntensity={0.6}
              metalness={0.7}
              roughness={0.3}
            />
          </mesh>
        )}
      </group>
    </group>
  );
}

// zombie prop is stable across UI re-renders (we mutate position in-place),
// so a referential-equality memo prevents zombies from reconciling on every
// HUD update (timer/score etc).
export default React.memo(Zombie, (prev, next) => prev.zombie === next.zombie);
